// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type RouterService from '@ember/routing/router';
import type { PaginatedCollection } from 'tio-common/types/ember-data';
import { service } from '@ember/service';
import { or } from 'tio-ui/utilities';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import pageTitle from 'ember-page-title/helpers/page-title';
import EligibilityFilesEligibilityCard from 'tio-common/components/eligibility-files/eligibility-card';
import Paginator from 'tio-ui/components/paginator';
import TioEligibilityUploadModal from 'tio-common/components/tio/eligibility-upload-modal';
import type EligibilityFileJobLogModel from 'tio-data/models/eligibility-file-job-log';
import {
  statusFromDisplayName,
  statusOptions,
} from 'tio-common/utils/eligibility-upload/status-lookups';
import type IndexController from 'tio-employee/controllers/authenticated/admin/eligibility-files/index';
import type IntlService from 'ember-intl/services/intl';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';

export interface S {
  Args: {
    model: PaginatedCollection<EligibilityFileJobLogModel>;
    controller: IndexController;
  };
}
export default class AuthenticatedAdminEligibilityFilesIndexRouteComponent extends Component<S> {
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;

  @tracked showUploadModal = false;

  get selectedStatus() {
    return this.intl.t(
      'common.eligibility_file_upload.status.' + (this.args.controller.status || 'ALL')
    );
  }

  convertFromReadableName(status: string) {
    return statusFromDisplayName(status) || 'ALL';
  }

  setSelectedStatus = (selectedStatus: string) => {
    if (selectedStatus === 'ALL' || !selectedStatus) {
      this.args.controller.status = '';
    } else {
      this.args.controller.status = this.convertFromReadableName(selectedStatus);
    }
  };

  setSelectedStartDate = (startDate: string) => {
    this.args.controller.startDate = startDate;
  };

  onSort = (sort: string) => {
    this.args.controller.sort = sort;
  };

  get sortOptions() {
    return [
      { key: '-created-at', label: this.intl.t('partner.eligibility_files.sort.newest_first') },
      { key: 'created-at', label: this.intl.t('partner.eligibility_files.sort.oldest_first') },
      { key: 'file-name', label: this.intl.t('partner.eligibility_files.sort.file_name_asc') },
      { key: '-file-name', label: this.intl.t('partner.eligibility_files.sort.file_name_desc') },
      { key: 'status', label: this.intl.t('partner.eligibility_files.sort.status_asc') },
      { key: '-status', label: this.intl.t('partner.eligibility_files.sort.status_desc') },
      { key: 'start-time', label: this.intl.t('partner.eligibility_files.sort.start_time_asc') },
      { key: '-start-time', label: this.intl.t('partner.eligibility_files.sort.start_time_desc') },
    ];
  }

  handleSortChange = (sortKey: string | null) => {
    if (sortKey) {
      this.args.controller.sort = sortKey;
    }
  };

  toggleUploadEligibilityFileModal = () => {
    this.showUploadModal = !this.showUploadModal;
  };

  closeModal = () => {
    this.showUploadModal = false;
  };

  previousPage = () => {
    this.args.controller.page = Math.max(1, this.args.controller.page - 1);
  };

  nextPage = () => {
    ++this.args.controller.page;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  get totalPages() {
    const { model, controller } = this.args;
    const meta = model?.meta;

    if (meta?.page?.total) {
      return Math.ceil(meta.page.total / controller.limit);
    }
    return 0;
  }

  <template>
    {{pageTitle (t "partner.eligibility_files.eligibility_files")}}

    <VStack>
      <Header>{{t "partner.eligibility_files.eligibility_files"}}</Header>

      {{! Filters Section }}
      <Section>
        <:body>
          <div class="flex flex-wrap items-end gap-4">
            <Input
              data-test-id="filter-by-date-input"
              @label={{t "partner.eligibility_files.filter_by_date"}}
              @type="date"
              @value={{@controller.startDate}}
              @onChange={{this.setSelectedStartDate}}
              class="min-w-48"
            />
            <Select
              @label={{t "partner.eligibility_files.filter_by_status"}}
              @items={{statusOptions}}
              @selected={{this.selectedStatus}}
              @onChange={{this.setSelectedStatus}}
              @clearable={{true}}
              class="min-w-48"
            />
            <Button
              @appearance="outlined"
              @intent="primary"
              {{on "click" this.toggleUploadEligibilityFileModal}}
            >
              {{t "partner.eligibility_files.upload_eligibility_file"}}
            </Button>
          </div>
        </:body>
      </Section>

      {{! Files List Section }}
      <Section>
        <:header>
          {{#if @model.length}}
            {{or @model.meta.page.total 0}}
            {{t "partner.eligibility_files.total_files"}}
          {{/if}}
        </:header>
        <:actions>
          <Select
            @label={{t "partner.eligibility_files.sort_by"}}
            @items={{this.sortOptions}}
            @selected={{@controller.sort}}
            @onChange={{this.handleSortChange}}
            class="min-w-48"
          />
        </:actions>
        <:body>
          <div class="space-y-4">
            {{#if @model.length}}
              {{#each @model as |item|}}
                <EligibilityFilesEligibilityCard
                  @showRoute="authenticated.admin.eligibility-files.show"
                  @indexRoute="authenticated.admin.eligibility-files.index"
                  @item={{item}}
                />
              {{/each}}
            {{else}}
              <div class="text-center py-12 tio-fg-muted">
                {{t "partner.eligibility_files.no_files_found"}}
              </div>
            {{/if}}
          </div>

          {{#if @model.length}}
            <div class="tio-flex-center mt-8">
              <Paginator
                @simple={{true}}
                @currentPage={{@controller.page}}
                @totalItems={{or @model.meta.page.total 0}}
                @pageSize={{@controller.limit}}
                @onChange={{this.changePaginatorPage}}
              />
            </div>
          {{/if}}
        </:body>
      </Section>
    </VStack>
    {{#if this.showUploadModal}}
      <TioEligibilityUploadModal
        {{! @glint-expect-error: need to unify partial commpany types from session }}
        @company={{this.partner.company}}
        @cancel={{this.closeModal}}
        @toggle={{this.toggleUploadEligibilityFileModal}}
        @showRoute="authenticated.admin.eligibility-files.show"
      />
    {{/if}}
  </template>
}
